/* Copyright (c) 2020-present CloudIO Inc */

import { Platform } from '../../platform/Platform';
import { baseComponentManager } from '../../registry/Components';
import { ComponentManagerFn } from '../../registry/Component.type';
import { ItemType } from '../../registry/ComponentRegistry';
import { defaultValue } from './KanbanItem';

const KanbanItemManager: ComponentManagerFn<'KanbanItem'> = (
  platform: Platform,
) => {
  return {
    ...baseComponentManager<'KanbanItem'>(platform, 'KanbanItem'),
    type: 'KanbanItem',
    name: 'Kanban Item',
    isChildAllowed: (page, item, childType) =>
      (['Icon', 'IconButton'] as ItemType[]).includes(childType),
    defaultValue,
    props: {
      title: {
        label: 'Title',
        type: 'expression',
        required: true,
        returnType: async () => 'string',
      },
      value: {
        label: 'Value',
        type: 'expression',
        required: true,
        returnType: async () => 'string',
      },
    },
    getDisplayText: (item) => item.props.title.value ?? '',
    category: 'Data',
    icon: { iconName: 'ballot' },
    description: `**Overview**<br/>
The Kanban Item is the visual template used to render individual cards within a Kanban Board. It acts as a "Blueprint" or "Renderer."
When you add a Kanban Item to a board, you are not creating a single card; you are defining how every record in your dataset should look. The board takes this single definition and repeats it for every record in your Data Store, dynamically filling in the details.

**1. Card Anatomy (Configuration)**<br/>
Every card needs a consistent structure to remain readable. The Kanban Item enforces a simple two-part layout, populated by dynamic expressions.
* **Title:** The primary identifier of the card (e.g., Task Name, Ticket ID). (Requirement: Mandatory. Every card must have a headline).
* **Expression Example:** \`$.taskName\`
* **Value:** Secondary metadata displayed below the title (e.g., Due Date, Assignee Name, Priority). (Requirement: Mandatory. Provides context without opening the card).
* **Expression Example:** \`Due: \${$.dueDate}\`

**2. Visual Composition**<br/>
To ensure the board remains performant and draggable, the content allowed inside a card is intentionally restricted. You cannot build complex forms inside a card; it is meant for "glanceable" information.
* **Allowed Children:**
    * **Icon:** Use this to add visual cues (e.g., a "Flag" icon for high priority).
    * **Icon Button:** Use this to add quick actions (e.g., an "Edit" pencil or "Delete" trash can).

**3. The Mental Model**<br/>
* **You Define:** One Kanban Item.
* **We Render:** One hundred cards.
* **Concept:** Think of the Kanban Item as a "Cookie Cutter." You shape it once, and the Kanban Board uses it to stamp out a card for every row in your database.

**4. Integration**<br/>
* **Parent Requirement:** This component allows no placement flexibility. It must be a direct child of a **Kanban Board**.
* **Context Awareness:** Although you define it once, each instance of the card "knows" which record it represents. If you add an Icon Button inside a Kanban Item and attach an OnClick trigger, that trigger will automatically receive the data of the specific card that was clicked.`,
  };
};

export default KanbanItemManager;
